import { Loader2, MessageSquare } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import type { SmsRecipientDto } from "~/lib/api";
import { fetchSmsRecipients, sendCodeBySms } from "~/lib/code-sms";

/** Human labels for the clinical role shown next to each candidate's name. */
const CLINICAL_ROLE_LABELS: Record<SmsRecipientDto["clinicalRole"], string> = {
  consultant: "Consultant",
  registrar: "Registrar",
  sho: "SHO",
  fy1: "FY1",
};

type RecipientsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; recipients: SmsRecipientDto[] }
  | { kind: "error"; message: string };

/**
 * "Send via SMS" affordance for the one-time handover code (HAN-66). Opens a
 * dialog that lists the ward's other members, with members who have no phone
 * number on file disabled, and texts the code to the chosen one. The code is
 * always on screen regardless, so a send failure is a soft error (toast +
 * inline message), never a blocker.
 */
export function SendCodeSmsDialog({
  sessionId,
  code,
}: {
  sessionId: string;
  code: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<RecipientsState>({ kind: "idle" });
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Load the candidate list the first time the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setState({ kind: "loading" });
    void fetchSmsRecipients(sessionId).then((result) => {
      if (cancelled) return;
      if (result.status === "ok") {
        setState({ kind: "ready", recipients: result.data });
      } else if (result.status === "denied") {
        setState({
          kind: "error",
          message: "Only the doctor who started this handover can do this.",
        });
      } else if (result.status === "not-found") {
        setState({ kind: "error", message: "This session is no longer available." });
      } else {
        setState({ kind: "error", message: "Couldn't load your team. Please try again." });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, sessionId]);

  const handleSend = useCallback(async () => {
    if (!selectedUserId) return;
    setSending(true);
    setSendError(null);
    const result = await sendCodeBySms(sessionId, selectedUserId, code);
    setSending(false);
    if (result.status === "error") {
      setSendError(result.message);
      return;
    }
    if (!result.data.sent) {
      // The request was valid but the provider rejected delivery — keep the
      // dialog open so they can retry or pick someone else.
      setSendError("Couldn't send the SMS just now. Read the code out, or try again.");
      return;
    }
    toast.success(`Code texted to ${result.data.recipientName}.`);
    setOpen(false);
  }, [selectedUserId, sessionId, code]);

  // Reset transient state whenever the dialog closes.
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setSelectedUserId(null);
      setSendError(null);
      setState({ kind: "idle" });
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline">
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          Send via SMS
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Text the code to a team member</DialogTitle>
          <DialogDescription>
            Pick a colleague on this ward. Only those with a phone number on file can be texted.
          </DialogDescription>
        </DialogHeader>

        {state.kind === "loading" ? (
          <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading your team…
          </p>
        ) : null}

        {state.kind === "error" ? (
          <Alert variant="destructive">
            <AlertDescription>{state.message}</AlertDescription>
          </Alert>
        ) : null}

        {state.kind === "ready" && state.recipients.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            There&rsquo;s no one else on this ward to text the code to.
          </p>
        ) : null}

        {state.kind === "ready" && state.recipients.length > 0 ? (
          <div className="space-y-2 py-2">
            <Label htmlFor="sms-recipient">Team member</Label>
            <Select
              value={selectedUserId ?? undefined}
              onValueChange={(value) => {
                setSelectedUserId(value);
                setSendError(null);
              }}
            >
              <SelectTrigger id="sms-recipient">
                <SelectValue placeholder="Choose a team member" />
              </SelectTrigger>
              <SelectContent>
                {state.recipients.map((recipient) => (
                  <SelectItem
                    key={recipient.userId}
                    value={recipient.userId}
                    disabled={!recipient.hasPhone}
                  >
                    {recipient.fullName} · {CLINICAL_ROLE_LABELS[recipient.clinicalRole]}
                    {recipient.hasPhone ? "" : " (no phone)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {sendError ? (
          <Alert variant="destructive">
            <AlertDescription>{sendError}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSend} disabled={!selectedUserId || sending}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Send code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
